import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from 'react-oidc-context';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { getApiUrl } from '../config/api';
import AdminPromptsTab from './AdminPromptsTab';

/**
 * Used only if `GET /api/admin/grants` omits `known_agent_ids` (older backend).
 * Prefer the API list — it stays aligned with `KNOWN_AGENT_IDS_ORDERED` in `backend/auth/rbac.py`.
 */
const FALLBACK_KNOWN_AGENT_IDS: readonly string[] = [
  'gpt',
  'm365_copilot',
  'docs',
  'research',
  'perplexity_research',
  'psa_kb',
  'hyperautomation',
  'noc_kb',
  'prospecting',
];

/** Checked by default on "New grant" — matches `DEFAULT_BOOTSTRAP_AGENT_ORDER` in `backend/auth/rbac.py`. */
const DEFAULT_TRUE_ON_ADD = new Set(['gpt', 'm365_copilot', 'docs', 'research', 'psa_kb', 'noc_kb']);

/** Backend `admin_api_denial` includes this when the verified JWT has no email claims — try id_token next. */
const ADMIN_NO_EMAIL_IN_TOKEN_REF = 'ADMIN_NO_EMAIL_IN_TOKEN';

function formatApiDetail(data: { detail?: unknown }, fallback: string): string {
  const d = data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map(x =>
        typeof x === 'object' && x !== null && 'msg' in x
          ? String((x as { msg: unknown }).msg)
          : JSON.stringify(x),
      )
      .join('; ');
  }
  if (d != null && typeof d === 'object') return JSON.stringify(d);
  return fallback;
}

type GrantEntry = {
  email: string;
  agents: string[];
  updated_at?: string;
  updated_by?: string;
};

function defaultAgentChecks(ids: string[]): Record<string, boolean> {
  return Object.fromEntries(ids.map(id => [id, DEFAULT_TRUE_ON_ADD.has(id)]));
}

function sortAgentsForDisplay(agents: string[], catalogOrder: string[]): string[] {
  const order = new Map(catalogOrder.map((id, i) => [id, i]));
  return [...agents].sort((a, b) => {
    const ia = order.has(a) ? (order.get(a) as number) : 1000 + a.charCodeAt(0);
    const ib = order.has(b) ? (order.get(b) as number) : 1000 + b.charCodeAt(0);
    if (ia !== ib) return ia - ib;
    return a.localeCompare(b);
  });
}

const AdminGrantsApp: React.FC = () => {
  const auth = useAuth();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirmDialog('dark');
  const accessToken = auth.user?.access_token || '';
  const idToken = auth.user?.id_token || '';
  const bearerCandidates = useMemo(() => {
    const xs = [accessToken, idToken].filter(Boolean);
    return [...new Set(xs)];
  }, [accessToken, idToken]);

  const [adminTab, setAdminTab] = useState<'grants' | 'prompts'>('grants');

  useEffect(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      if (t === 'prompts') setAdminTab('prompts');
      if (t === 'grants') setAdminTab('grants');
    } catch {
      /* ignore */
    }
  }, []);

  const [entries, setEntries] = useState<GrantEntry[]>([]);
  const [knownAgentIds, setKnownAgentIds] = useState<string[]>([...FALLBACK_KNOWN_AGENT_IDS]);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleRow = (email: string) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [formMode, setFormMode] = useState<'closed' | 'add' | 'edit'>('closed');
  const [formEmail, setFormEmail] = useState('');
  const [formAgents, setFormAgents] = useState<Record<string, boolean>>(() => defaultAgentChecks([...FALLBACK_KNOWN_AGENT_IDS]));
  /** Edit-only: agents on the row not returned in `known_agent_ids` (stale catalog or hand-edited Dynamo). */
  const [formExtraAgents, setFormExtraAgents] = useState<string[]>([]);

  const knownIdSet = useMemo(() => new Set(knownAgentIds), [knownAgentIds]);

  const fetchAdmin = useCallback(
    async (url: string, init: RequestInit = {}) => {
      let last: Response | null = null;
      for (const bearer of bearerCandidates) {
        const merged: Record<string, string> = {
          ...((init.headers as Record<string, string>) || {}),
          Authorization: `Bearer ${bearer}`,
        };
        last = await fetch(url, { ...init, headers: merged });
        if (last.ok) return last;
        if (last.status === 401) continue;
        if (last.status === 403) {
          const probe = await last.clone().json().catch(() => ({}));
          const msg = formatApiDetail(probe as { detail?: unknown }, '');
          if (msg.includes(ADMIN_NO_EMAIL_IN_TOKEN_REF)) continue;
        }
        return last;
      }
      return last!;
    },
    [bearerCandidates],
  );

  const fetchAll = useCallback(async () => {
    if (bearerCandidates.length === 0) return;
    setError(null);
    setLoading(true);
    try {
      const r = await fetchAdmin(getApiUrl('/admin/grants'));
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(formatApiDetail(data, `Load failed (${r.status})`));
        setEntries([]);
        return;
      }
      const rawIds = data.known_agent_ids;
      const ids =
        Array.isArray(rawIds) && rawIds.length > 0
          ? rawIds.map((x: unknown) => String(x).trim()).filter(Boolean)
          : [...FALLBACK_KNOWN_AGENT_IDS];
      setKnownAgentIds(ids);

      const list = Array.isArray(data.entries) ? data.entries : [];
      setEntries(
        list.map((row: Record<string, unknown>) => ({
          email: String(row.email || ''),
          agents: Array.isArray(row.agents) ? row.agents.map(String) : [],
          updated_at: row.updated_at != null ? String(row.updated_at) : undefined,
          updated_by: row.updated_by != null ? String(row.updated_by) : undefined,
        })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [bearerCandidates, fetchAdmin]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(e => e.email.toLowerCase().includes(q));
  }, [entries, filter]);

  // Reset to page 1 whenever the filter or page size changes
  useEffect(() => {
    setPage(1);
  }, [filter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginated = useMemo(
    () => filtered.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filtered, safePage, pageSize],
  );

  const openAdd = () => {
    setFormMode('add');
    setFormEmail('');
    setFormAgents(defaultAgentChecks(knownAgentIds));
    setFormExtraAgents([]);
    setToast(null);
    setError(null);
  };

  const openEdit = (row: GrantEntry) => {
    setFormMode('edit');
    setFormEmail(row.email);
    const next: Record<string, boolean> = Object.fromEntries(knownAgentIds.map(id => [id, false]));
    for (const id of knownAgentIds) {
      if (row.agents.includes(id)) next[id] = true;
    }
    setFormAgents(next);
    setFormExtraAgents(row.agents.filter(a => !knownIdSet.has(a)));
    setToast(null);
    setError(null);
  };

  const closeForm = () => {
    setFormMode('closed');
    setFormEmail('');
    setFormAgents(defaultAgentChecks(knownAgentIds));
    setFormExtraAgents([]);
  };

  const saveForm = async () => {
    const q = formEmail.trim().toLowerCase();
    if (!q || !q.includes('@')) {
      setError('Enter a valid email.');
      return;
    }
    setError(null);
    const chosen = knownAgentIds.filter(id => formAgents[id]);
    try {
      const r = await fetchAdmin(getApiUrl('/admin/grants'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: q, agents: chosen }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(formatApiDetail(data, `Save failed (${r.status})`));
        return;
      }
      setToast(`Saved ${q}: ${chosen.join(', ') || '(deny all)'}`);
      closeForm();
      await fetchAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const deleteRow = async (email: string) => {
    const q = email.trim().toLowerCase();
    if (
      !(await confirmAction({
        title: 'Delete grant',
        message: `Delete grant row for ${q}? With RBAC on, a new default row may be created on their next login.`,
        confirmLabel: 'Delete',
        variant: 'danger',
      }))
    ) {
      return;
    }
    setError(null);
    try {
      const r = await fetchAdmin(
        `${getApiUrl('/admin/grants')}?email=${encodeURIComponent(q)}`,
        { method: 'DELETE' },
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(formatApiDetail(data, `Delete failed (${r.status})`));
        return;
      }
      setToast(`Deleted ${q}`);
      if (formMode === 'edit' && formEmail.trim().toLowerCase() === q) closeForm();
      await fetchAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  useEffect(() => {
    if (formMode !== 'closed') return;
    setFormAgents(defaultAgentChecks(knownAgentIds));
  }, [knownAgentIds, formMode]);

  if (bearerCandidates.length === 0) {
    return (
      <div className="h-screen bg-gray-950 text-gray-200 flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-12 h-12 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>
          <p className="text-sm text-gray-400">Sign in to manage agent grants.</p>
        </div>
      </div>
    );
  }

  const grantModal =
    formMode !== 'closed' &&
    createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        role="presentation"
        onClick={() => closeForm()}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="grant-modal-title"
          className="w-full max-w-lg rounded-2xl border border-gray-700/60 bg-gray-900 text-gray-100 shadow-[0_0_60px_rgba(0,0,0,0.6)] max-h-[90vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          <div className="px-6 py-5 border-b border-gray-700/60 bg-gray-800/40 rounded-t-2xl shrink-0 flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-cyan-900/60 border border-cyan-700/50 flex items-center justify-center shrink-0">
              <svg className="w-3.5 h-3.5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                {formMode === 'add'
                  ? <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  : <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                }
              </svg>
            </div>
            <div>
              <h2 id="grant-modal-title" className="text-sm font-semibold text-white">
                {formMode === 'add' ? 'Add grant' : 'Edit grant'}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">None checked = deny all agents for this email.</p>
            </div>
          </div>
          <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0 space-y-5">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">Email</label>
              <input
                className="w-full rounded-lg border border-gray-700 bg-gray-800/80 px-3 py-2 text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                value={formEmail}
                onChange={e => setFormEmail(e.target.value)}
                placeholder="user@company.com"
                disabled={formMode === 'edit'}
                autoComplete="off"
              />
              {formMode === 'edit' && (
                <p className="text-xs text-gray-600 mt-1.5">Email is fixed for edit. Delete the row to change address.</p>
              )}
            </div>
            {formExtraAgents.length > 0 && (
              <div className="rounded-lg border border-amber-800/50 bg-amber-950/30 px-4 py-3 text-xs text-amber-200 flex gap-2">
                <svg className="w-4 h-4 shrink-0 text-amber-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <span>
                  This row includes agents not in the current catalog:{' '}
                  <code className="text-amber-300">{formExtraAgents.join(', ')}</code>.
                  Extras are dropped on save unless added to the backend allowlist.
                </span>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-3 uppercase tracking-wider">Allowed agents</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5 max-h-[min(50vh,22rem)] overflow-y-auto pr-1">
                {knownAgentIds.map(id => (
                  <label key={id} className="flex items-center gap-2.5 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={!!formAgents[id]}
                      onChange={e => setFormAgents(prev => ({ ...prev, [id]: e.target.checked }))}
                      className="rounded border-gray-600 bg-gray-800 text-cyan-600 focus:ring-cyan-500/40"
                    />
                    <span className="font-mono text-xs text-gray-300 group-hover:text-white transition-colors">{id}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-700/60 flex flex-wrap gap-2 shrink-0 justify-end bg-gray-800/20 rounded-b-2xl">
            <button
              type="button"
              onClick={() => closeForm()}
              className="px-4 py-2 rounded-lg border border-gray-600/60 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-700/50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void saveForm()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-sm text-white font-medium transition-colors shadow-sm"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              Save
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-gray-950 text-gray-100">
      {/* Cyan accent line */}
      <div className="h-[3px] w-full shrink-0 bg-gradient-to-r from-cyan-800 via-cyan-400 to-cyan-800" />

      <style>{`
        @keyframes loading-bar {
          0%   { transform: translateX(-100%) scaleX(0.3); }
          50%  { transform: translateX(0%)    scaleX(0.7); }
          100% { transform: translateX(100%)  scaleX(0.3); }
        }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .skeleton {
          background: linear-gradient(90deg, #1f2937 25%, #374151 50%, #1f2937 75%);
          background-size: 800px 100%;
          animation: shimmer 1.4s infinite linear;
          border-radius: 4px;
        }
        /* Thin custom scrollbar */
        ::-webkit-scrollbar            { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track      { background: transparent; }
        ::-webkit-scrollbar-thumb      { background: #374151; border-radius: 999px; }
        ::-webkit-scrollbar-thumb:hover{ background: #4b5563; }
        ::-webkit-scrollbar-corner     { background: transparent; }
        * { scrollbar-width: thin; scrollbar-color: #374151 transparent; }
      `}</style>

      {/* Centred column */}
      <div className="flex-1 overflow-hidden flex flex-col w-full max-w-6xl mx-auto px-4 md:px-8 py-5">

        {/* Card — same gradient border as chat composer */}
        <div
          className="flex-1 overflow-hidden rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)]"
          style={{
            background: 'linear-gradient(90deg, #0099FF 0%, #47C7B2 50%, #C5E654 100%)',
            padding: '1px',
          }}
        >
        <div className="h-full overflow-hidden flex flex-col rounded-2xl bg-gray-900">

          {/* ── Header ── */}
          <div className="shrink-0 bg-gray-800/50 border-b border-gray-700/50 px-6 py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Brand */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-cyan-900/60 border border-cyan-700/40 flex items-center justify-center shrink-0 shadow-inner">
                  <svg className="w-4.5 h-4.5 w-[18px] h-[18px] text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.955 11.955 0 003 12c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.249-8.25-3.286z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-base font-semibold text-white leading-tight">Admin Console</h1>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Requires <code className="text-cyan-400 bg-cyan-950/60 px-1 py-0.5 rounded text-[10px]">ADMIN_EMAILS</code>
                  </p>
                </div>
              </div>

              {/* Controls — single row, right-aligned */}
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {/* Tab switcher */}
                <div className="flex gap-1 bg-gray-900/70 rounded-xl p-1 border border-gray-700/50">
                  <button
                    type="button"
                    onClick={() => setAdminTab('grants')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap ${adminTab === 'grants' ? 'bg-cyan-700 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'}`}
                  >
                    Agent grants
                  </button>
                  <button
                    type="button"
                    onClick={() => setAdminTab('prompts')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap ${adminTab === 'prompts' ? 'bg-cyan-700 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'}`}
                  >
                    Prompt moderation
                  </button>
                </div>

                {/* Divider */}
                <div className="h-5 w-px bg-gray-700/60 shrink-0" />

                {/* Action buttons */}
                {adminTab === 'grants' && (
                  <>
                    <button
                      type="button"
                      onClick={() => void fetchAll()}
                      disabled={loading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-700/80 hover:bg-gray-600/80 text-sm text-gray-200 border border-gray-600/40 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                      </svg>
                      {loading ? 'Loading…' : 'Refresh'}
                    </button>
                    <button
                      type="button"
                      onClick={openAdd}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-sm text-white border border-cyan-600/40 transition-colors shadow-sm whitespace-nowrap"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      New grant
                    </button>
                  </>
                )}
                <a
                  href="/"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-600/40 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-700/50 transition-colors whitespace-nowrap"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                  </svg>
                  Back to chat
                </a>
              </div>
            </div>
          </div>

          {/* ── Card body ── */}
          <div className="flex-1 overflow-hidden flex flex-col px-6 py-5">

            {adminTab === 'prompts' && (
              <div className="flex-1 overflow-auto">
                <AdminPromptsTab fetchAdmin={fetchAdmin} />
              </div>
            )}

            {adminTab === 'grants' && (
              <div className="flex-1 overflow-hidden flex flex-col gap-4">

                {/* Filter bar */}
                <div className="shrink-0 flex flex-col sm:flex-row gap-3 sm:items-center">
                  <div className="relative flex-1 max-w-sm">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                    <input
                      type="search"
                      className="w-full rounded-lg border border-gray-700 bg-gray-800/80 pl-9 pr-3 py-2 text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-600 transition"
                      placeholder="Filter by email…"
                      value={filter}
                      onChange={e => setFilter(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <span className="text-xs text-gray-500 shrink-0 tabular-nums">
                    {filtered.length === 0
                      ? `0 of ${entries.length}`
                      : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)} of ${filtered.length}${entries.length !== filtered.length ? ` (${entries.length} total)` : ''}`}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-gray-500">Per page</span>
                    <select
                      className="rounded-lg border border-gray-700 bg-gray-800/80 px-2 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-600 transition"
                      value={pageSize}
                      onChange={e => setPageSize(Number(e.target.value))}
                    >
                      {[10, 25, 50, 100].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Table */}
                <div className="flex-1 overflow-hidden flex flex-col rounded-xl border border-gray-700/60">
                  {/* Progress bar */}
                  <div className="h-0.5 w-full bg-gray-800 overflow-hidden shrink-0">
                    {loading && (
                      <div className="h-full bg-cyan-500" style={{ animation: 'loading-bar 1.4s ease-in-out infinite' }} />
                    )}
                  </div>
                  {/* Scrollable table */}
                  <div className={`flex-1 overflow-auto transition-opacity duration-200 ${loading && entries.length > 0 ? 'opacity-50' : ''}`}>
                    <table className="w-full text-sm text-left">
                      <thead className="sticky top-0 z-10 bg-gray-800 border-b border-gray-700/60">
                        <tr>
                          <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400 w-px whitespace-nowrap">Email</th>
                          <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400 text-center">Agents</th>
                          <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hidden lg:table-cell w-36">Updated</th>
                          <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hidden xl:table-cell w-28">By</th>
                          <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400 text-right w-36">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800/70">
                        {loading && entries.length === 0 ? (
                          Array.from({ length: 6 }).map((_, i) => (
                            <tr key={i} className="animate-pulse">
                              <td className="px-4 py-3.5"><div className="skeleton h-3 w-48 rounded" /></td>
                              <td className="px-4 py-3.5">
                                <div className="flex gap-1.5">
                                  <div className="skeleton h-5 w-14 rounded-full" />
                                  <div className="skeleton h-5 w-10 rounded-full" />
                                  <div className="skeleton h-5 w-16 rounded-full" />
                                </div>
                              </td>
                              <td className="px-4 py-3.5 hidden lg:table-cell"><div className="skeleton h-3 w-32 rounded" /></td>
                              <td className="px-4 py-3.5 hidden xl:table-cell"><div className="skeleton h-3 w-24 rounded" /></td>
                              <td className="px-4 py-3.5 text-right">
                                <div className="inline-flex gap-1.5 justify-end">
                                  <div className="skeleton h-6 w-14 rounded-lg" />
                                  <div className="skeleton h-6 w-16 rounded-lg" />
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : filtered.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-16 text-center">
                              <div className="flex flex-col items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center">
                                  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                                  </svg>
                                </div>
                                <p className="text-sm text-gray-500 max-w-xs">
                                  {entries.length === 0
                                    ? 'No grant rows yet. Users may appear after their first visit when RBAC auto-provision runs.'
                                    : 'No rows match this filter.'}
                                </p>
                                {entries.length === 0 && (
                                  <button
                                    type="button"
                                    onClick={openAdd}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-xs text-white transition-colors"
                                  >
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                    </svg>
                                    Add first grant
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ) : (
                          paginated.map(row => {
                            const sorted = sortAgentsForDisplay(row.agents, knownAgentIds);
                            const unknown = row.agents.filter(a => !knownIdSet.has(a));
                            return (
                              <tr key={row.email} className="group hover:bg-gray-800/50 transition-colors duration-100">
                                <td className="px-4 py-3 font-mono text-xs text-cyan-300/90 break-all sm:whitespace-nowrap align-top">
                                  {row.email}
                                </td>
                                <td className="px-4 py-3 align-middle text-center">
                                  {sorted.length === 0 ? (
                                    <span className="text-gray-600 text-xs italic">none</span>
                                  ) : (() => {
                                    const isExpanded = expandedRows.has(row.email);
                                    return (
                                      <div className="flex flex-col items-center">
                                        {/* Collapsed pill button */}
                                        <button
                                          type="button"
                                          onClick={() => toggleRow(row.email)}
                                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-normal text-cyan-400/70 bg-cyan-950/30 ring-1 ring-cyan-800/30 hover:bg-cyan-950/50 hover:text-cyan-400 hover:ring-cyan-800/50 transition-all"
                                        >
                                          <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                                          </svg>
                                          {sorted.length} agent{sorted.length !== 1 ? 's' : ''}
                                          <svg
                                            className={`w-3 h-3 shrink-0 transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`}
                                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                                          >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                          </svg>
                                        </button>
                                        {/* Expanded badge list */}
                                        {isExpanded && (
                                          <div className="flex flex-wrap gap-1 mt-2">
                                            {sorted.map(id => (
                                              <span
                                                key={id}
                                                className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium font-mono ${unknown.includes(id)
                                                  ? 'bg-amber-900/40 text-amber-200 ring-1 ring-amber-700/50'
                                                  : 'bg-gray-800 text-gray-300 ring-1 ring-gray-600/60'}`}
                                                title={unknown.includes(id) ? 'Not in server catalog' : undefined}
                                              >
                                                {id}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell align-top tabular-nums">
                                  {row.updated_at ? row.updated_at.slice(0, 19).replace('T', ' ') : <span className="text-gray-700">—</span>}
                                </td>
                                <td className="px-4 py-3 text-gray-500 text-xs hidden xl:table-cell truncate max-w-[8rem] align-top">
                                  {row.updated_by || <span className="text-gray-700">—</span>}
                                </td>
                                <td className="px-4 py-3 text-right align-top">
                                  <div className="flex gap-1.5 justify-end opacity-60 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap">
                                    <button
                                      type="button"
                                      onClick={() => openEdit(row)}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs text-cyan-400 bg-cyan-950/60 hover:bg-cyan-900/70 ring-1 ring-cyan-800/50 hover:ring-cyan-700/60 transition-all"
                                    >
                                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                                      </svg>
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => void deleteRow(row.email)}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs text-red-400 bg-red-950/60 hover:bg-red-900/70 ring-1 ring-red-900/50 hover:ring-red-800/60 transition-all"
                                    >
                                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                      </svg>
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="shrink-0 flex items-center justify-center gap-1 flex-wrap py-0.5">
                    <button type="button" onClick={() => setPage(1)} disabled={safePage === 1} aria-label="First page"
                      className="px-2 py-1 rounded-lg border border-gray-700 text-xs text-gray-400 hover:bg-gray-700 hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">«</button>
                    <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} aria-label="Previous page"
                      className="px-2 py-1 rounded-lg border border-gray-700 text-xs text-gray-400 hover:bg-gray-700 hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">‹</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 2)
                      .reduce<(number | '…')[]>((acc, n, idx, arr) => {
                        if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push('…');
                        acc.push(n);
                        return acc;
                      }, [])
                      .map((item, idx) =>
                        item === '…' ? (
                          <span key={`ellipsis-${idx}`} className="px-1.5 text-xs text-gray-600">…</span>
                        ) : (
                          <button key={item} type="button" onClick={() => setPage(item as number)}
                            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${safePage === item ? 'bg-cyan-700 text-white ring-1 ring-cyan-600/50' : 'border border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-gray-200'}`}
                            aria-current={safePage === item ? 'page' : undefined}
                          >{item}</button>
                        ),
                      )}
                    <button type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} aria-label="Next page"
                      className="px-2 py-1 rounded-lg border border-gray-700 text-xs text-gray-400 hover:bg-gray-700 hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">›</button>
                    <button type="button" onClick={() => setPage(totalPages)} disabled={safePage === totalPages} aria-label="Last page"
                      className="px-2 py-1 rounded-lg border border-gray-700 text-xs text-gray-400 hover:bg-gray-700 hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">»</button>
                  </div>
                )}

                {/* Status messages */}
                <div className="shrink-0 space-y-2">
                  {error && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-950/50 border border-red-800/40 text-sm text-red-300">
                      <svg className="w-4 h-4 shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                      </svg>
                      {error}
                    </div>
                  )}
                  {toast && !error && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-950/50 border border-emerald-800/40 text-sm text-emerald-300">
                      <svg className="w-4 h-4 shrink-0 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {toast}
                    </div>
                  )}
                </div>

              </div>
            )}

          </div>
        </div>
        </div>
      </div>

      {grantModal}
      {confirmDialog}
    </div>
  );
};

export default AdminGrantsApp;
